import { motion } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Building2, CheckCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const statusColors = {
  aktiv: "bg-report-success/10 text-report-success border-report-success/20",
  pilot: "bg-report-gold/10 text-report-gold border-report-gold/20",
  geplant: "bg-muted text-muted-foreground border-border",
};

export function DataTable() {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-muted/30">
      <div className="container px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.dataTable.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.dataTable.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-6xl mx-auto bg-card rounded-2xl border border-border shadow-sm overflow-hidden"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">{t.dataTable.headers.bank}</TableHead>
                  <TableHead className="font-semibold">{t.dataTable.headers.country}</TableHead>
                  <TableHead className="font-semibold">{t.dataTable.headers.model}</TableHead>
                  <TableHead className="font-semibold">{t.dataTable.headers.hours}</TableHead>
                  <TableHead className="font-semibold">{t.dataTable.headers.status}</TableHead>
                  <TableHead className="font-semibold">{t.dataTable.headers.result}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.dataTable.items.map((bank) => (
                  <TableRow key={bank.name} className="hover:bg-muted/30 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-accent/10">
                          <Building2 className="h-4 w-4 text-accent" />
                        </div>
                        <span className="font-medium">{bank.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">{bank.country}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-pre-wrap">{bank.model}</TableCell>
                    <TableCell className="font-medium">{bank.hours}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[bank.status]}>
                        {t.dataTable.statusLabels[bank.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-report-success" />
                        <span className="text-muted-foreground">{bank.highlight}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
